/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import {
  Backdrop,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Topheader from "../../Layout/header/header";
import { useEffect, useState } from "react";
import WarningIcon from "@mui/icons-material/Warning";
import VerifiedIcon from "@mui/icons-material/Verified";
import "./userProfile.css";
import DeveloperAgreement from "./developerAgreement";
import APIManager from "../../Services/ApiManager/ApiManager";
import CheckIcon from "@mui/icons-material/Check";
import Moment from "moment";
import { useNavigate } from "react-router-dom";
import PhoneInput, {
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import PopupDialog from "../../Components/PopupDialog";
import { jwtDecode } from "jwt-decode";
import { getConfig } from "../../config";

export default function UserProfile() {
  const [loader, setLoader] = useState(false);
  const [account, setAccount] = useState("");
  const [corporateContactName, setCorporateContactName] = useState("");
  const [corporateContactEmail, setCorporateContactEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [agreementStatus, setAgreementStatus] = useState(false);
  const [agreementDate, setAgreementDate] = useState("");
  const [companyRegNumber, setCompanyRegNumber] = useState("");
  const [inputRegNumberErrMsg, setInputRegNumberErrMsg] = useState("");
  const [inputCountryRegErrMsg, setInputCountryRegErrMsg] = useState("");
  const [inputCompNameErrMsg, setInputCompNameErrMsg] = useState("");
  const [inputCompWebsiteErrMsg, setInputCompWebsiteErrMsg] = useState("");
  const [countryOfReg, setCountryOfReg] = useState("");
  const [readOnlyValue, setReadOnlyValue] = useState(true);
  const [showSaveFlag, setShowSaveFlag] = useState(false);
  const [disableSubmitAgreement, setDisableSubmitAgreement] = useState(false);
  const [agreement_accepted, setAgreement_accepted] = useState(false);
  const [inputCrpNameErrMsg, setInputCrpNameErrMsg] = useState("");
  const [inputCrpEmailErrMsg, setInputCrpEmailErrMsg] = useState("");
  const [inputAddressErrMsg, setInputAddressErrMsg] = useState("");
  const [inputPrivacyLinkErrMsg, setInputPrivacyLinkErrMsg] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const handleBackClick = () => {
    if (getConfig().REACT_APP_PORTAL === "domain-approver") {
      // ✅ Set default Group Apps status when navigating from User Profile
      sessionStorage.setItem("domain-approver-selectedTab", "0");
      sessionStorage.setItem(
        "domain-approver-activeStatus",
        "SUBMITTED_FOR_PRODUCTION"
      );
      navigate(`/ui/domain-approver/applications`);
    } else if (getConfig().REACT_APP_PORTAL === "global-admin") {
      navigate(`/ui/harman-admin/dashboard`);
    } else if (isOemAdmin) {
      navigate(`/ui/oem-admin/dashboard`);
    } else {
      navigate(`/ui/developer/applications`);
    }
  };
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.com$/;
  const urlRegex =
    /^(https?|ftp):\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.com\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
  const registrationNumberRegex = /^[a-zA-Z0-9\- /]+$/; // Alphanumeric, hyphen, space, slash
  const alphabetRegex = /^[a-zA-Z\s]+$/;

  const handleClose = () => {
    setAgreement_accepted(false);
    setDisableSubmitAgreement(false);
    setOpen(false);
  };

  const handleEdit = () => {
    setReadOnlyValue(false);
    setShowSaveFlag(true);
  };

  const validateCorporateContactName = (value) => {
    if (!value) {
      setInputCrpNameErrMsg("Corporate Contact Name is required");
      return false;
    }
    setInputCrpNameErrMsg("");
    return true;
  };

  const validateCorporateContactEmail = (value) => {
    if (!value) {
      setInputCrpEmailErrMsg("Corporate Contact Email is required");
      return false;
    }
    if (!emailRegex.test(value)) {
      setInputCrpEmailErrMsg("Please enter a valid email address");
      return false;
    }
    setInputCrpEmailErrMsg("");
    return true;
  };

  const validateAddress = (value) => {
    if (!value) {
      setInputAddressErrMsg("Address is required");
      return false;
    }
    if (value.length <= 10) {
      setInputAddressErrMsg(`Entered: ${value.length}, Required more than: 10`);
      return false;
    }
    setInputAddressErrMsg("");
    return true;
  };

  const validateCompanyName = (value) => {
    if (!value) {
      setInputCompNameErrMsg("Company Name is required");
      return false;
    }
    setInputCompNameErrMsg("");
    return true;
  };

  const validateCompanyWebsite = (value) => {
    if (!value) {
      setInputCompWebsiteErrMsg("Company Website is required");
      return false;
    }
    setInputCompWebsiteErrMsg("");
    return true;
  };

  const validatePrivacyPolicy = (value) => {
    if (!value) {
      setInputPrivacyLinkErrMsg("Privacy Policy is required");
      return false;
    }
    if (!urlRegex.test(value)) {
      setInputPrivacyLinkErrMsg("Please enter a valid URL");
      return false;
    }
    setInputPrivacyLinkErrMsg("");
    return true;
  };

  const validateRegistrationNumber = (value) => {
    if (!value) {
      setInputRegNumberErrMsg("Registration Number is required");
      return false;
    }
    if (!registrationNumberRegex.test(value)) {
      setInputRegNumberErrMsg("Registration number must contain only alphanumeric characters, hyphens, spaces, or slashes");
      return false;
    }
    setInputRegNumberErrMsg("");
    return true;
  };

  const validateCountryOfRegistration = (value) => {
    if (!value) {
      setInputCountryRegErrMsg("Country of Registration is required");
      return false;
    }
    if (!alphabetRegex.test(value)) {
      setInputCountryRegErrMsg("Country name must contain only letters");
      return false;
    }
    setInputCountryRegErrMsg("");
    return true;
  };

  const validateInput = (value, fieldName) => {
    const validationMap = {
      "Corporate Contact Name": validateCorporateContactName,
      "Corporate Contact Email": validateCorporateContactEmail,
      "Address": validateAddress,
      "Company Name": validateCompanyName,
      "Company Website": validateCompanyWebsite,
      "Privacy Policy": validatePrivacyPolicy,
      "Registration Number": validateRegistrationNumber,
      "Country of Registration": validateCountryOfRegistration,
    };

    const validator = validationMap[fieldName];
    return validator ? validator(value) : false;
  };

  const handleCorporateContactName = (event) => {
    setCorporateContactName(event.target.value);
    validateInput(event.target.value, "Corporate Contact Name");
  };

  const handleCorporateContactEmail = (event) => {
    setCorporateContactEmail(event.target.value);
    validateInput(event.target.value, "Corporate Contact Email");
  };

  const handlePhoneNumber = (event) => {
    setPhoneNumber(event.target.value);
  };
  const handleCompanyName = (event) => {
    setCompanyName(event.target.value);
    validateInput(event.target.value, "Company Name");
  };
  const handleCompanyWebsite = (event) => {
    setCompanyWebsite(event.target.value);
    validateInput(event.target.value, "Company Website");
  };
  const handleAddress = (event) => {
    setAddress(event.target.value);
    validateInput(event.target.value, "Address");
  };

  const handlePrivacyPolicy = (event) => {
    setPrivacyPolicy(event.target.value);
    validateInput(event.target.value, "Privacy Policy");
  };

  const handleCompanyRegNum = (event) => {
    setCompanyRegNumber(event.target.value);
    validateInput(event.target.value, "Registration Number");
  };

  const handleCountryOfReg = (event) => {
    setCountryOfReg(event.target.value);
    validateInput(event.target.value, "Country of Registration");
  };

  const handleCancelProfile = () => {
    setReadOnlyValue(true);
    setShowSaveFlag(false);
    if (getConfig().REACT_APP_PORTAL === "seller") {
      navigate(`/ui/developer/applications`);
    } else if (getConfig().REACT_APP_PORTAL === "domain-approver") {
      // ✅ Set default Group Apps status when navigating from User Profile
      sessionStorage.setItem("domain-approver-selectedTab", "0");
      sessionStorage.setItem(
        "domain-approver-activeStatus",
        "SUBMITTED_FOR_PRODUCTION"
      );
      navigate(`/ui/domain-approver/applications`);
    } else if (getConfig().REACT_APP_PORTAL === "oem-admin") {
      navigate(`/ui/oem-admin/dashboard`);
    }
  };

  const handleAgreementChange = (event) => {
    setAgreement_accepted(event.target.checked);

    if (
      event.target.checked &&
      corporateContactEmail !== undefined &&
      corporateContactName !== undefined &&
      phoneNumber !== undefined &&
      address !== undefined &&
      privacyPolicy !== undefined
    ) {
      setDisableSubmitAgreement(false);
    }
  };

  const handleProfileSave = () => {
    setLoader(true);

    let saveProfileData = {
      corporateContactName: corporateContactName,
      corporateContactEmail: corporateContactEmail,
      companyName: companyName,
      companyWebsite: companyWebsite,
      address1: address,
      phoneNumber: phoneNumber,
      companyPrivacyPolicy: privacyPolicy,
      companyRegNumber: companyRegNumber,
      countryOfReg: countryOfReg,
    };

    if (isOemAdmin || isGlobalAdmin || isDomainApprover) {
      APIManager.patchCognitoAdminSelf(saveProfileData)
        .then((json) => {
          setLoader(false);
          setOpenSnackbar(true);
          setSnackMsg(`User profile is saved successfully`);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          setComponentName("appError");
          setErrorMessage(error.message);
          setHeaderMessage(
            error.message === "jwt expired" ? "401 Token Expired" : "Error"
          );
          setOpenErrorPopup(true);
          setLoader(false);
        });
    } else {
      APIManager.patchCognitoSelf(saveProfileData)
        .then((json) => {
          setLoader(false);
          setOpenSnackbar(true);
          setSnackMsg(`User profile is saved successfully`);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          setComponentName("appError");
          setErrorMessage(error.message);
          setHeaderMessage(
            error.message === "jwt expired" ? "401 Token Expired" : "Error"
          );
          setOpenErrorPopup(true);
          setLoader(false);
        });
    }
    setReadOnlyValue(true);
    setShowSaveFlag(false);
  };

  const handleAcceptAgreement = () => {
    setLoader(true);
    const acceptDateValue = String(new Date());

    let saveProfileData = {
      corporateContactName: corporateContactName,
      corporateContactEmail: corporateContactEmail,
      companyName: companyName,
      companyWebsite: companyWebsite,
      companyRegNumber: companyRegNumber,
      countryOfReg: countryOfReg,
      address1: address,
      phoneNumber: phoneNumber,
      companyPrivacyPolicy: privacyPolicy,
      igniteStoreAgreementStatus: true,
      igniteStoreAgreementDate: Moment(acceptDateValue).format("YYYY-MM-DD"),
    };

    if (isOemAdmin || isGlobalAdmin || isDomainApprover) {
      APIManager.patchCognitoAdminSelf(saveProfileData).then((json) => {
        setLoader(false);
        sessionStorage.removeItem("agreementPending");
      });
      setOpen(false);
    } else {
      APIManager.patchCognitoSelf(saveProfileData).then((json) => {
        setLoader(false);
        sessionStorage.removeItem("agreementPending");
      });
      setOpen(false);
    }
  };

  const handleViewDeveloperAgreement = async () => {
    if (
      corporateContactEmail === undefined ||
      corporateContactName === undefined ||
      companyName === undefined ||
      companyWebsite === undefined ||
      phoneNumber === undefined ||
      address === undefined ||
      privacyPolicy === undefined ||
      agreement_accepted === true
    ) {
      setDisableSubmitAgreement(true);
    } else {
      setDisableSubmitAgreement(false);
    }

    setOpen(true);
  };
  const handlePopUpClose = () => {
    setOpenErrorPopup(false);
  };
  const getPhoneNumberError = () => {
    if (!phoneNumber) return "Phone number required";
    if (!isValidPhoneNumber(phoneNumber)) return "Invalid phone number";
    return undefined;
  };
  const isOemAdmin = sessionStorage.getItem("customPortal") === "oem-admin";
  const isGlobalAdmin = getConfig().REACT_APP_PORTAL === "global-admin";
  const isDomainApprover = getConfig().REACT_APP_PORTAL === "domain-approver"
  useEffect(() => {
    setLoader(true);
    if (isOemAdmin || isGlobalAdmin || isDomainApprover) {
      if (sessionStorage.getItem("idpLogin") === "true") {
        setLoader(false);
        const decoded = jwtDecode(sessionStorage.getItem("id_token"));
        setAccount(decoded.preferred_username);
        setCompanyWebsite(decoded.website);
        setCompanyName(decoded["custom:companyName"]);
        setPortfolio(sessionStorage.getItem("portfolioId"));
      } else {
        APIManager.getCognitoAdminSelf()
          .then((json) => {
            setAccount(json.results[0].userName);
            setCorporateContactName(json.results[0].corporateContactName);
            setCorporateContactEmail(json.results[0].corporateContactEmail);
            setPhoneNumber(json.results[0].phoneNumber);
            setAddress(json.results[0].address1);
            setPrivacyPolicy(json.results[0].privacyPolicy);
            setCompanyRegNumber(json.results[0].companyRegNumber);
            setCountryOfReg(json.results[0].countryOfReg);
            setCompanyName(json.results[0].companyName);
            setCompanyWebsite(json.results[0].companyWebsite);
            setAgreementStatus(json.results[0].igniteStoreAgreementStatus);
            setAgreementDate(json.results[0].igniteStoreAgreementDate);
            setLoader(false);
          })
          .catch((error) => {
            setComponentName("appError");
            setErrorMessage(error.message);
            if (error.message === "jwt expired") {
              setHeaderMessage("401 Token Expired");
            } else {
              setHeaderMessage("Error");
            }
            setOpenErrorPopup(true);
            setLoader(false);
          });
      }
    } else {
      APIManager.getCognitoSelf()
        .then((json) => {
          setAccount(json.results[0].userName);
          setCorporateContactName(json.results[0].corporateContactName);
          setCorporateContactEmail(json.results[0].corporateContactEmail);
          setPhoneNumber(json.results[0].phoneNumber);
          setAddress(json.results[0].address1);
          setCompanyRegNumber(json.results[0].companyRegNumber);
          setCountryOfReg(json.results[0].countryOfReg);
          if (validateInput(json.results[0].privacyPolicy, "Privacy Policy")) {
            setPrivacyPolicy(json.results[0].privacyPolicy);
          } else {
            setPrivacyPolicy("");
          }
          setCompanyName(json.results[0].companyName);
          setCompanyWebsite(json.results[0].companyWebsite);
          setAgreementStatus(json.results[0].igniteStoreAgreementStatus);
          setAgreementDate(json.results[0].igniteStoreAgreementDate);
          setLoader(false);
        })
        .catch((error) => {
          setComponentName("appError");
          setErrorMessage(error.message);
          if (error.message === "jwt expired") {
            setHeaderMessage("401 Token Expired");
          } else {
            setHeaderMessage("Error");
          }
          setOpenErrorPopup(true);
          setLoader(false);
        });
    }
  }, []);

  return (
    <>
      <div className="appListContainer">
        <div>
          <Topheader />
        </div>

        {loader && (
          <Backdrop
            sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
            open={loader}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <div className="wholeContainer">
          <button className="backToApplication" onClick={handleBackClick} style={{background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
            {" "}
            <IconButton sx={{ borderRadius: "4px" }} onClick={handleBackClick}>
              <ArrowBackIcon />
              <Typography sx={{ color: "#2A2C2D" }}>
                {getConfig().REACT_APP_PORTAL === "domain-approver"
                  ? "Applications"
                  : "Dashboard"}
              </Typography>
            </IconButton>{" "}
          </button>
          <div className="containercls">
            <div className="contentCls">
              <div className="contentHeaderCls">
                {getConfig().REACT_APP_PORTAL === "global-admin" ||
                getConfig().REACT_APP_PORTAL === "seller" ? (
                  <div className="headerProfileCls">Profile Info</div>
                ) : (
                  <div className="headerProfileCls">My Profile</div>
                )}
                {!showSaveFlag &&
                  sessionStorage.getItem("idpLogin") !== "true" && (
                    <div>
                      <Button
                        className="editBtnCls"
                        variant="outlined"
                        onClick={handleEdit}
                      >
                        Edit
                      </Button>
                    </div>
                  )}
                {showSaveFlag && (
                  <div>
                    <Button
                      style={{
                        background: "#1214151A",
                        textTransform: "none",
                        height: "32px",
                        width: "116px",
                        color: "#2A2C2D",
                        fontWeight: "500",
                        fontSize: "14px",
                        lineHeight: "16.41px",
                        boxShadow: "none",
                      }}
                      variant="contained"
                      onClick={handleCancelProfile}
                    >
                      Cancel
                    </Button>
                    &nbsp;&nbsp;
                    <Button
                      style={{
                        background: "221.2 83.2% 53.3%",
                        textTransform: "none",
                        height: "32px",
                        width: "116px",
                        fontWeight: "500",
                        fontSize: "14px",
                        lineHeight: "16.41px",
                        boxShadow: "none",
                      }}
                      variant="contained"
                      onClick={handleProfileSave}
                      disabled={
                        !(corporateContactName &&
                        corporateContactName.length > 0 &&
                        inputCrpNameErrMsg === "" &&
                        corporateContactEmail &&
                        corporateContactEmail.length > 0 &&
                        (isGlobalAdmin
                          ? true
                          : companyName &&
                            companyName.length > 0 &&
                            inputCompNameErrMsg === "" &&
                            companyWebsite &&
                            companyWebsite.length > 0 &&
                            inputCompWebsiteErrMsg === "") &&
                        inputCrpEmailErrMsg === "" &&
                        phoneNumber &&
                        phoneNumber.length > 0 &&
                        isValidPhoneNumber(phoneNumber) === true &&
                        address &&
                        address.length > 0 &&
                        inputAddressErrMsg === "" &&
                        privacyPolicy &&
                        privacyPolicy.length > 0 &&
                        inputPrivacyLinkErrMsg === "" &&
                        (isOemAdmin || isGlobalAdmin || isDomainApprover
                          ? true
                          : countryOfReg &&
                            countryOfReg.length > 0 &&
                            inputCountryRegErrMsg === "" &&
                            companyRegNumber &&
                            companyRegNumber.length > 0 &&
                            inputRegNumberErrMsg === ""))
                      }
                    >
                      Save
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <form>
                  <div className="accountControlCls">
                    <FormControl
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "48%",
                      }}
                    >
                      <label className="labelName" htmlFor="account">
                        Account <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        className="inputCls"
                        disabled
                        fullWidth
                        autoComplete="off"
                        id="account"
                        value={account}
                        slotProps={{
                          input: {
                            readOnly: readOnlyValue,
                          },
                        }}
                      />
                    </FormControl>
                    {sessionStorage.getItem("idpLogin") !== "true" && (
                      <FormControl
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "48%",
                        }}
                      >
                        <label className="labelName" htmlFor="corporateContactName">
                          {getConfig().REACT_APP_PORTAL === "global-admin" ||
                          getConfig().REACT_APP_PORTAL === "seller"
                            ? "Contact Name "
                            : "Corporate Contact Name "}<span style={{ color: "red", display: isOemAdmin ? "none" : undefined }}>*</span>
                        </label>
                        <TextField
                          className="inputCls"
                          disabled={readOnlyValue}
                          fullWidth
                          autoComplete="off"
                          id="corporateContactName"
                          onChange={handleCorporateContactName}
                          value={corporateContactName}
                          slotProps={{
                            input: {
                              readOnly: readOnlyValue,
                            },
                          }}
                        />
                        {inputCrpNameErrMsg && (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            {inputCrpNameErrMsg}
                          </span>
                        )}
                      </FormControl>
                    )}
                    {sessionStorage.getItem("idpLogin") === "true" &&
                      getConfig().REACT_APP_PORTAL === "oem-admin" && (
                        <FormControl
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "48%",
                          }}
                        >
                        <label className="labelName" htmlFor="portfolio">
                          Portfolio <span style={{ color: "red", display: isOemAdmin ? "none" : undefined }}>*</span>
                          </label>
                          <TextField
                            className="inputCls"
                            disabled={readOnlyValue}
                            fullWidth
                            autoComplete="off"
                            id="portfolio"
                            value={portfolio}
                            slotProps={{
                              input: {
                                readOnly: readOnlyValue,
                              },
                            }}
                          />
                          {inputCrpNameErrMsg && (
                            <span style={{ color: "red", fontSize: "12px" }}>
                              {inputCrpNameErrMsg}
                            </span>
                          )}
                        </FormControl>
                      )}
                  </div>
                  <div className="accountControlCls">
                    {sessionStorage.getItem("idpLogin") !== "true" && (
                      <FormControl
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "48%",
                        }}
                      >
                        <label className="labelName" htmlFor="corporateContactEmail">
                          {getConfig().REACT_APP_PORTAL === "global-admin" ||
                          getConfig().REACT_APP_PORTAL === "seller"
                            ? "Contact Email "
                            : "Corporate Contact Email "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          className="inputCls"
                          type="email"
                          disabled={readOnlyValue}
                          autoComplete="off"
                          fullWidth
                          id="corporateContactEmail"
                          value={corporateContactEmail}
                          onChange={handleCorporateContactEmail}
                          slotProps={{
                            input: {
                              readOnly: readOnlyValue,
                            },
                          }}
                        />
                        {inputCrpEmailErrMsg && (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            {inputCrpEmailErrMsg}
                          </span>
                        )}
                      </FormControl>
                    )}
                    {sessionStorage.getItem("idpLogin") !== "true" && (
                      <FormControl
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "48%",
                        }}
                      >
                        <label className="labelName" htmlFor="phoneNumber">
                          Phone Number <span style={{ color: "red" }}>*</span>
                        </label>
                        {readOnlyValue && (
                          <TextField
                            className="inputCls"
                            disabled={readOnlyValue}
                            fullWidth
                            autoComplete="off"
                            id="phoneNumber"
                            value={phoneNumber}
                            onChange={handlePhoneNumber}
                            slotProps={{
                              input: {
                                readOnly: readOnlyValue,
                              },
                            }}
                          />
                        )}
                        {!readOnlyValue && (
                          <PhoneInput
                            international
                            value={phoneNumber}
                            onChange={setPhoneNumber}
                            countryCallingCodeEditable={false}
                            error={getPhoneNumberError()}
                          />
                        )}
                        {phoneNumber && !isValidPhoneNumber(phoneNumber) && (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            Invalid phone number
                          </span>
                        )}
                      </FormControl>
                    )}
                  </div>
                  {!isGlobalAdmin && (
                    <div className="accountControlCls">
                      <FormControl
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "48%",
                        }}
                      >
                        <label className="labelName" htmlFor="corporateCompanyName">
                          Company Name <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          className="inputCls"
                          disabled={isOemAdmin ? readOnlyValue : "true"}
                          fullWidth
                          autoComplete="off"
                          id="corporateCompanyName"
                          value={companyName}
                          onChange={handleCompanyName}
                          slotProps={{
                            input: {
                              readOnly: readOnlyValue,
                            },
                          }}
                        />
                        {inputCompNameErrMsg && (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            {inputCompNameErrMsg}
                          </span>
                        )}
                      </FormControl>

                      <FormControl
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "48%",
                        }}
                      >
                        <label className="labelName" htmlFor="corportateCompanyWebsite">
                          Company Website <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          className="inputCls"
                          autoComplete="off"
                          disabled={isOemAdmin ? readOnlyValue : "true"}
                          fullWidth
                          id="corportateCompanyWebsite"
                          value={companyWebsite}
                          onChange={handleCompanyWebsite}
                          slotProps={{
                            input: {
                              readOnly: readOnlyValue,
                            },
                          }}
                        />
                        {inputCompWebsiteErrMsg && (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            {inputCompWebsiteErrMsg}
                          </span>
                        )}
                      </FormControl>
                    </div>
                  )}
                  {!isOemAdmin && !isGlobalAdmin && !isDomainApprover &&(
                    <div className="accountControlCls">
                      {
                        <FormControl
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "48%",
                          }}
                        >
                          <label className="labelName" htmlFor="companyRegNumber">
                            Registration Number <span style={{ color: "red" }}>*</span>
                          </label>
                          <TextField
                            className="inputCls"
                            disabled={readOnlyValue}
                            fullWidth
                            autoComplete="off"
                            id="companyRegNumber"
                            value={companyRegNumber}
                            onChange={handleCompanyRegNum}
                            slotProps={{
                              input: {
                                readOnly: readOnlyValue,
                              },
                            }}
                          />
                          {inputRegNumberErrMsg && (
                            <span style={{ color: "red", fontSize: "12px" }}>
                              {inputRegNumberErrMsg}
                            </span>
                          )}
                        </FormControl>
                      }

                      <FormControl
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "48%",
                        }}
                      >
                        <label className="labelName" htmlFor="countryOfReg">
                          Country of Registration <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          className="inputCls"
                          autoComplete="off"
                          disabled={readOnlyValue}
                          fullWidth
                          id="countryOfReg"
                          value={countryOfReg}
                          onChange={handleCountryOfReg}
                          slotProps={{
                            input: {
                              readOnly: readOnlyValue,
                            },
                          }}
                        />
                        {inputCountryRegErrMsg && (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            {inputCountryRegErrMsg}
                          </span>
                        )}
                      </FormControl>
                    </div>
                  )}

                  <div className="accountControlCls">
                    {sessionStorage.getItem("idpLogin") !== "true" && (
                      <FormControl
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <label className="labelName" htmlFor="address">
                          Address <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          className="inputCls"
                          disabled={readOnlyValue}
                          autoComplete="off"
                          fullWidth
                          id="address"
                          value={address}
                          onChange={handleAddress}
                          slotProps={{
                            input: {
                              readOnly: readOnlyValue,
                            },
                          }}
                        />
                        {inputAddressErrMsg && (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            {inputAddressErrMsg}
                          </span>
                        )}
                      </FormControl>
                    )}
                  </div>
                  <div className="accountControlCls">
                    {sessionStorage.getItem("idpLogin") !== "true" && (
                      <FormControl
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <label className="labelName" htmlFor="privacyPolicy">
                          Privacy Policy <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          className="inputCls"
                          disabled={readOnlyValue}
                          autoComplete="off"
                          placeholder="https://"
                          fullWidth
                          sx={{
                            ".MuiInputBase-input": {
                              fontSize: "14px !important",
                            },
                          }}
                          id="privacyPolicy"
                          value={privacyPolicy}
                          onChange={handlePrivacyPolicy}
                          slotProps={{
                            input: {
                              readOnly: readOnlyValue,
                            },
                          }}
                        />
                        {inputPrivacyLinkErrMsg && (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            {inputPrivacyLinkErrMsg}
                          </span>
                        )}
                      </FormControl>
                    )}
                  </div>
                  {sessionStorage.getItem("idpLogin") !== "true" &&
                    !isDomainApprover &&
                    !isOemAdmin &&
                    !isGlobalAdmin && (
                      <div className="agreementMsg">
                        <div className="developerAgreementTxt">
                          Developer Agreement <span style={{ color: "red" }}>*</span>
                        </div>
                        {!agreementStatus && (
                          <div className="agreementNotAcceptedMsg agreementText">
                            <WarningIcon className="iconCls" />
                            <span>&nbsp;Not Accepted</span>
                          </div>
                        )}

                        {agreementStatus && (
                          <div className="agreementAcceptedMsg agreementText">
                            <VerifiedIcon className="iconCls" />
                            <span>&nbsp; Accepted</span>
                          </div>
                        )}
                        <button style={{background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
                          className="viewAgreementCls"
                          onClick={handleViewDeveloperAgreement}
                        >
                          View Developer Agreement
                        </button>
                      </div>
                    )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={open}
        maxWidth="md"
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Ignite Store Developer Agreement"}
        </DialogTitle>
        <DialogContent>
          <DeveloperAgreement />
        </DialogContent>

        {!agreementStatus && (
          <DialogActions style={{ justifyContent: "space-between" }}>
            <div className="checkAgreeCls">
              <Checkbox
                checked={agreement_accepted}
                size="small"
                onChange={handleAgreementChange}
                slotProps={{ input: { "aria-label": "controlled" } }}
              />
              <span>I agree to the updated Terms</span>
            </div>
            <div>
              <Button
                className="editBtnCls"
                variant="outlined"
                onClick={handleClose}
              >
                Disagree
              </Button>
              &nbsp;&nbsp;
              <Button
                disabled={disableSubmitAgreement}
                onClick={handleAcceptAgreement}
                style={{
                  background: "221.2 83.2% 53.3%",
                  textTransform: "none",
                  height: "32px",
                  width: "116px",
                  fontWeight: "500",
                  fontSize: "14px",
                  lineHeight: "16.41px",
                  boxShadow: "none",
                }}
                variant="contained"
              >
                Accept
              </Button>
            </div>
          </DialogActions>
        )}
        {agreementStatus && (
          <DialogActions className="acceptedDialogCls">
            <CheckIcon className="checkAcceptedCls" />
            <span className="checkBoxTextCls">
              Accepted on &nbsp;
              {Moment(agreementDate).format("MMM DD YYYY")}
            </span>
          </DialogActions>
        )}
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={handlePopUpClose}
      />
    </>
  );
}
